import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { ImagePlus, Check, Pencil } from "lucide-react";
import { Shell, Btn, Field, dateRange } from "@/components/album/ui";
import CoverView, { TEXT_COLORS, OVERLAYS } from "@/components/album/CoverView";
import { compressImage, createRequest } from "@/lib/api";
import FilterStep from "@/components/album/FilterStep";
import { Backdrop } from "@/components/theme/Backdrop";
import { EVENT_TYPES, FILTERS, typeOf } from "@/config/album";
import { WHATSAPP_NUMBER } from "@/config/pricing";
import { cn } from "@/lib/utils";

const STEPS = 6;
const today = new Date().toISOString().slice(0, 10);

const Label = ({ children }) => (
  <span className="font-sans text-xs uppercase tracking-wider text-[#8A8A8A]">{children}</span>
);

const SizeHint = ({ info, hasCover }) => {
  const base = "Ukuran ideal 1080 × 1920 px (rasio 9:16, foto tegak). Minimal 720 × 1280 px.";
  if (!info || !hasCover)
    return <p className="mt-3 font-sans text-xs leading-relaxed text-[#8A8A8A]">{base}</p>;
  const r = info.w / info.h;
  let ok = false;
  let msg;
  if (r > 0.75) msg = "Fotomu landscape, jadi sisi kiri dan kanan akan terpotong. Pakai foto tegak agar hasilnya terbaik.";
  else if (r > 0.62) msg = "Fotomu agak lebar, sedikit bagian samping akan terpotong.";
  else if (r < 0.4) msg = "Fotomu sangat tinggi, bagian atas dan bawah akan terpotong.";
  else {
    ok = true;
    msg = "Rasio fotomu sudah pas.";
  }
  const small = Math.min(info.w, info.h) < 720;
  return (
    <div className="mt-3 space-y-2">
      <p className={cn("flex items-start gap-2 border bg-white p-3 font-sans text-xs leading-relaxed", ok ? "border-[#E8E3D9] text-[#5C5C5C]" : "border-[#1C1C1C] text-[#1C1C1C]")}>
        {ok && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
        <span>{msg} <span className="text-[#8A8A8A]">({info.w} × {info.h} px)</span></span>
      </p>
      {small && <p className="font-sans text-xs text-[#8A8A8A]">Resolusi agak kecil, foto bisa terlihat kurang tajam di HP tamu.</p>}
      <p className="font-sans text-xs text-[#8A8A8A]">{base}</p>
    </div>
  );
};

export default function AlbumWizard() {
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({
    eventType: "wedding", eventName: "", couple: "", cover: "",
    textColor: "#FFFFFF", overlay: "medium", filter: "asli",
    allowGuestView: true, startDate: "", endDate: "",
  });
  const fileRef = useRef(null);
  const [info, setInfo] = useState(null); // ukuran asli foto cover
  const [sending, setSending] = useState(false);
  const et = typeOf(f.eventType); // semua teks mengikuti jenis acara
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const pickCover = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const bmp = await createImageBitmap(file);
      setInfo({ w: bmp.width, h: bmp.height });
      bmp.close?.();
      const url = await compressImage(file, 1200, 0.78);
      setF((p) => ({ ...p, cover: url }));
    } catch {
      toast.error("Foto tidak bisa dibaca. Coba foto lain.");
    }
  };

  const next = () => {
    if (step === 1 && !f.eventName.trim()) return toast.error("Isi nama acara dulu.");
    if (step === 2) {
      if (et.nameRequired && !f.couple.trim()) return toast.error(`Isi ${et.nameLabel.toLowerCase()} dulu.`);
      if (!f.cover) return toast.error("Unggah foto cover dulu.");
    }
    if (step === 4) {
      if (!f.startDate || !f.endDate) return toast.error("Pilih tanggal acara.");
      if (f.endDate < f.startDate) return toast.error("Tanggal selesai tidak boleh sebelum tanggal mulai.");
    }
    if (step === STEPS) return submit();
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (sending) return;
    setSending(true);
    let saved;
    try {
      saved = await createRequest(f);
    } catch (e) {
      setSending(false);
      return toast.error(e.message || "Permintaan gagal dikirim. Coba lagi.");
    }
    setSending(false);
    const fl = FILTERS.find((x) => x.id === f.filter) || FILTERS[0];
    const msg = `Halo DIRGA,

Saya ingin membuat album Ruang Kenang.

Jenis Acara:
${et.label}

Nama Acara:
${f.eventName}

${et.waName}:
${f.couple || "-"}

Tanggal Acara:
${dateRange(f)}

Nuansa Foto:
${fl.label}

Tamu boleh melihat foto tamu lain:
${f.allowGuestView ? "Ya" : "Tidak"}

Kode permintaan:
${saved.rid}

Terima kasih.`;
    const base = WHATSAPP_NUMBER ? `https://wa.me/${WHATSAPP_NUMBER}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
    setDone(true);
  };

  if (done)
    return (
      <Shell back={{ to: "/", label: "Beranda" }} className="max-w-xl pt-16 text-center">
        <Toaster position="top-center" />
      <Backdrop mode="overlay" />
        <Check className="mx-auto h-8 w-8 text-[#C6A56B]" strokeWidth={1.5} />
        <h1 className="mt-8 font-serif text-5xl font-light tracking-tight">Permintaan terkirim</h1>
        <p className="mt-6 font-sans text-sm leading-relaxed text-[#8A8A8A]">
          Admin DIRGA akan mengaktifkan albummu, lalu mengirim QR, link album, dan PIN lewat WhatsApp.
        </p>
      </Shell>
    );

  const titles = {
    1: ["Apa nama acaramu?", "Judul ini terlihat oleh semua tamu."],
    2: [et.coverTitle, "Foto ini jadi latar halaman yang dilihat tamu."],
    3: ["Izin melihat album", "Pilih apa yang boleh dilihat tamu."],
    4: ["Kapan acaranya?", "Tanggal ini tampil di cover album."],
    5: ["Rapikan desain cover", "Pilih warna tulisan yang jelas terbaca di atas fotomu."],
    6: ["Pilih nuansa foto", "Berlaku untuk foto yang diambil tamu. Cover tidak berubah, fotomu hanya dipakai sebagai contoh."],
  }[step];

  return (
    <Shell back={{ to: "/", label: "Batal" }} className="max-w-4xl pt-4">
      <Toaster position="top-center" />
      <Backdrop mode="overlay" />
      <div className="mb-8 flex gap-2" aria-label={`Langkah ${step} dari ${STEPS}`}>
        {Array.from({ length: STEPS }).map((_, i) => (
          <span key={i} className={cn("h-px flex-1 transition-colors duration-500", i < step ? "bg-[#C6A56B]" : "bg-[#E8E3D9]")} />
        ))}
      </div>

      <div className="grid items-start gap-10 lg:grid-cols-[1fr_auto] lg:gap-16">
        {/* PREVIEW: berubah setiap data diisi */}
        <div className="flex justify-center lg:order-2 lg:sticky lg:top-8">
          <div className="relative rounded-[30px] bg-[#1C1C1C] p-[6px] shadow-none">
            <div className="relative h-[352px] w-[176px] overflow-hidden rounded-[24px] bg-white lg:h-[520px] lg:w-[260px]">
              <div className="absolute left-0 top-0 h-[520px] w-[260px] origin-top-left scale-[0.677] lg:scale-100">
                <CoverView data={f} size="preview" />
              </div>
            </div>
            {step === 2 && f.cover && (
              <button type="button" onClick={() => fileRef.current?.click()} className="absolute -bottom-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 border border-[#1C1C1C] bg-white px-3 py-1.5 font-sans text-[10px] uppercase tracking-wider text-[#1C1C1C]">
                <Pencil className="h-3 w-3" /> Ubah
              </button>
            )}
          </div>
        </div>

        <div className="lg:order-1 lg:max-w-md">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }}>
              <h1 className="font-serif text-4xl font-light tracking-tight sm:text-5xl">{titles[0]}</h1>
              <p className="mt-3 font-sans text-sm text-[#8A8A8A]">{titles[1]}</p>

              <div className="mt-8 space-y-8">
                {step === 1 && (
                  <>
                    <div>
                      <Label>Jenis acara</Label>
                      <div className="mt-2 grid grid-cols-2 gap-3">
                        {EVENT_TYPES.map((t) => (
                          <button key={t.id} type="button" onClick={() => setF((p) => ({ ...p, eventType: t.id, couple: "" }))}
                            className={cn("border px-4 py-3 font-sans text-sm transition-colors", f.eventType === t.id ? "border-[#C6A56B] bg-[#FBF7EE]" : "border-[#E8E3D9] bg-white hover:border-[#B9B2A4]")}>{t.label}</button>
                        ))}
                      </div>
                    </div>
                    <Field label="Nama acara" placeholder={et.examples[0]} value={f.eventName} onChange={set("eventName")} hint="Bisa diubah nanti lewat admin." />
                    <div>
                      <Label>Contoh</Label>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {et.examples.map((t) => (
                          <button key={t} type="button" onClick={() => setF((p) => ({ ...p, eventName: t }))}
                            className={cn("border px-4 py-2 font-sans text-sm transition-colors", f.eventName === t ? "border-[#1C1C1C] bg-[#F4F2EC] text-[#1C1C1C]" : "border-[#E8E3D9] bg-white text-[#5C5C5C] hover:border-[#B9B2A4]")}>{t}</button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <Field
                      label={et.nameLabel}
                      placeholder={et.namePlaceholder}
                      value={f.couple} onChange={set("couple")}
                    />
                    <div>
                      <Btn variant="outline" className="w-full" onClick={() => fileRef.current?.click()}>
                        <ImagePlus className="h-4 w-4" /> {f.cover ? "Ganti sampul" : "Unggah sampul"}
                      </Btn>
                      <SizeHint info={info} hasCover={!!f.cover} />
                    </div>
                  </>
                )}

                {step === 3 &&
                  [[true, "Tamu boleh melihat semua foto", "Tamu bisa melihat foto dari tamu lain di album."],
                   [false, "Tamu hanya melihat fotonya sendiri", `Foto tamu lain hanya bisa dilihat ${et.owner}.`]].map(([val, t, d]) => (
                    <button key={t} type="button" onClick={() => setF((p) => ({ ...p, allowGuestView: val }))}
                      className={cn("block w-full border p-6 text-left transition-colors", f.allowGuestView === val ? "border-[#C6A56B] bg-[#FBF7EE]" : "border-[#E8E3D9] bg-white hover:border-[#B9B2A4]")}>
                      <span className="font-serif text-2xl">{t}</span>
                      <span className="mt-2 block font-sans text-sm text-[#5C5C5C]">{d}</span>
                    </button>
                  ))}

                {step === 4 && (
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                    <Field type="date" label="Mulai" min={today} value={f.startDate} onChange={(e) => setF((p) => ({ ...p, startDate: e.target.value, endDate: p.endDate && p.endDate < e.target.value ? e.target.value : p.endDate }))} />
                    <Field type="date" label="Selesai" min={f.startDate || today} value={f.endDate} onChange={set("endDate")} />
                  </div>
                )}

                {step === 5 && (
                  <>
                    <div>
                      <Label>Warna tulisan</Label>
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        {TEXT_COLORS.map((c) => (
                          <button key={c.id} type="button" onClick={() => setF((p) => ({ ...p, textColor: c.value }))} aria-label={c.label} title={c.label}
                            className={cn("h-10 w-10 border transition-all", f.textColor.toLowerCase() === c.value.toLowerCase() ? "border-[#1C1C1C] ring-1 ring-[#1C1C1C] ring-offset-2" : "border-[#D8D2C6]")}
                            style={{ background: c.value }} />
                        ))}
                        <label className="relative flex h-10 cursor-pointer items-center gap-2 border border-[#E8E3D9] bg-white px-3 font-sans text-xs text-[#5C5C5C]">
                          <span className="h-4 w-4 border border-[#D8D2C6]" style={{ background: f.textColor }} /> Warna lain
                          <input type="color" value={f.textColor} onChange={set("textColor")} className="absolute inset-0 cursor-pointer opacity-0" />
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label>Lapisan di atas foto</Label>
                      <div className="mt-3 grid grid-cols-4 gap-2">
                        {OVERLAYS.map((o) => (
                          <button key={o.id} type="button" onClick={() => setF((p) => ({ ...p, overlay: o.id }))}
                            className={cn("border px-2 py-3 font-sans text-xs transition-colors", f.overlay === o.id ? "border-[#C6A56B] bg-[#FBF7EE]" : "border-[#E8E3D9] bg-white hover:border-[#B9B2A4]")}>{o.label}</button>
                        ))}
                      </div>
                      <p className="mt-3 font-sans text-xs leading-relaxed text-[#8A8A8A]">Tulisan terang diberi lapisan gelap, tulisan gelap diberi lapisan terang, supaya tetap terbaca.</p>
                    </div>
                  </>
                )}

                {step === 6 && (
                  <FilterStep cover={f.cover} value={f.filter} onChange={(id) => setF((p) => ({ ...p, filter: id }))} />
                )}
              </div>
            </motion.div>
          </AnimatePresence>

          <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickCover} />

          <div className="mt-10 flex gap-4">
            {step > 1 && <Btn variant="outline" onClick={() => setStep((s) => s - 1)}>Kembali</Btn>}
            <Btn onClick={next} disabled={sending} className="flex-1 sm:flex-none">{step === STEPS ? (sending ? "Mengirim…" : "Kirim ke Admin") : "Lanjut"}</Btn>
          </div>
          {step === STEPS && <p className="mt-4 font-sans text-xs text-[#8A8A8A]">Permintaan dikirim lewat WhatsApp. Album aktif setelah admin mengonfirmasi.</p>}
        </div>
      </div>
    </Shell>
  );
}
